import React from 'react'
import i18n from '@pureartisan/simple-i18n'
import shallow from 'zustand/shallow'
import { makeStyles } from '@material-ui/core/styles'
import useStore from '../store'
import clsx from 'clsx'
import { STATES } from './../../../../constants/map'
import _ from 'lodash'
import ChartContainer from './ChartContainer'
import { SwitchControl } from '../Controls'
import { Box, FormControlLabel } from '@material-ui/core'
import {
  getMetroNameFromFips,
  getStateNameFromFips,
} from '../utils'
const poi_data = require('../../../../constants/poi_data.json')

const useStyles = makeStyles(theme => ({
  mainChart: {
    position: 'absolute',
    left: '80px',
    top: '80px',
    '&.noActiveRegion .chart-subtitle': {
      fontStyle: 'italic',
    },
  },
  overlaysSection: {
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    borderTop: theme.extras.styles.faintBorder,
    paddingTop: theme.spacing(2),
    '& .MuiFormControlLabel-root': {
      paddingLeft: theme.spacing(2),
    },
  },
}))

// some POI sites have nobody in certain quintiles, which breaks the barchart
const infillPoiData = data => {
  ;[0, 1, 2, 3, 4].forEach(g => {
    if (!data.some(({ grp }) => Number(grp) === g)) {
      data.push({ grp: String(g) })
    }
  })
  return data.sort((a, b) => Number(a.grp) - Number(b.grp))
}

const processData = (
  data,
  norm,
  year,
  activeRegion,
  activePoi,
) => {
  if (!!activePoi) {
    const normName = {
      n: 'nation',
      s: 'states',
      m: 'metros',
    }[norm]
    const dataYear = year
    const raw = _.get(
      data.barcharts_poi.data,
      [`20${dataYear}`, normName, activePoi],
      [],
    )
    return infillPoiData(raw)
  }

  switch (norm) {
    case 'n':
      return _.get(
        data.barcharts.data,
        [`20${year}`, 'nation'],
        [],
      )

    case 's':
      if (!!activeRegion) {
        return _.get(
          data.barcharts.data,
          [
            `20${year}`,
            'states',
            STATES[activeRegion].abbr,
          ],
          [],
        )
      }

    case 'm':
      if (!!activeRegion) {
        return _.get(
          data.barcharts.data,
          [`20${year}`, 'metros', activeRegion],
          [],
        )
      }
  }

  return []
}

const MainChart = ({}) => {
  const {
    activeYear,
    activeNorm,
    activePointLayers,
    POIModeActive,
    activePOI,
    // legendPanel,
    showDensityOverlays,
    toggleDensityOverlays,
    slideoutPanel,
    activeRegion,
    remoteJson,
    setStoreValues,
  } = useStore(
    state => ({
      activeYear: state.activeYear,
      activeNorm: state.activeNorm,
      POIModeActive: state.POIModeActive,
      activePOI: state.activePOI,
      activePointLayers: state.activePointLayers,
      showDensityOverlays: state.showDensityOverlays,
      toggleDensityOverlays: state.toggleDensityOverlays,
      // legendPanel: state.legendPanel,
      slideoutPanel: state.slideoutPanel,
      activeRegion: state.activeRegion,
      remoteJson: state.remoteJson,
      setStoreValues: state.setStoreValues,
    }),
    shallow,
  )

  const open =
    !!remoteJson &&
    !!remoteJson.barcharts &&
    !!slideoutPanel.active &&
    slideoutPanel.panel === 'chart'

  const data = open
    ? processData(
        remoteJson,
        activeNorm,
        activeYear,
        activeRegion,
        POIModeActive && activePOI,
      )
    : []

  const classes = useStyles()

  const handleClose = () =>
    setStoreValues({
      slideoutPanel: {
        ...slideoutPanel,
        active: false,
      },
    })

  // Handle changes to the point layer checkboxes
  const handleActivePointLayers = demo => {
    const layers = activePointLayers.slice()
    const ind = layers.indexOf(demo)
    if (ind >= 0) {
      layers.splice(ind, 1)
    } else {
      layers.push(demo)
    }
    setStoreValues({
      activePointLayers: layers,
    })
  }

  const getChartSubtitle = norm => {
    if (POIModeActive && activePOI) {
      const POI = poi_data.sites.find(
        s => s.id === activePOI,
      ).name
      return {
        subtitle: i18n.translate('WKKF_CHART_TITLE', {
          POI,
          norm: i18n.translate(
            `LEGEND_NORMLY_${activeNorm}`,
          ),
        }),
      }
    }
    switch (norm) {
      case 'n':
        const subtitle = i18n.translate('GENERIC_THE_US')
        return { subtitle }
      case 's':
        if (!!activeRegion) {
          const subtitle =
            getStateNameFromFips(activeRegion) ||
            i18n.translate('GENERIC_THE_STATE')
          return { subtitle }
        } else {
          const subtitle = i18n.translate(
            'NO_STATE_ACTIVATED',
          )
          return { subtitle, noActiveRegion: true }
        }
      case 'm':
        if (!!activeRegion) {
          const subtitle = getMetroNameFromFips(
            activeRegion,
          )
          return { subtitle }
        } else {
          const subtitle = i18n.translate(
            'NO_METRO_ACTIVATED',
          )
          return { subtitle, noActiveRegion: true }
        }
    }
  }

  const { subtitle, noActiveRegion } = getChartSubtitle(
    activeNorm,
  )
  if (!open) return null
  return (
    <ChartContainer
      id="main-chart"
      className={clsx(classes.mainChart, {
        noActiveRegion,
      })}
      data={data}
      activeBars={activePointLayers}
      handleActivePointLayers={handleActivePointLayers}
      handleClose={handleClose}
      title={i18n.translate(`MAIN_CHART_TITLE`)}
      subtitle={subtitle}
    >
      {/* density overlay switch */}
      <Box
        className={clsx(
          classes.overlaysSection,
          'screenshot-ignore',
        )}
      >
        <FormControlLabel
          control={
            <SwitchControl
              checked={showDensityOverlays}
              onChange={toggleDensityOverlays}
            />
          }
          label={i18n.translate(`CHART_DEMO`)}
        />
      </Box>
    </ChartContainer>
  )
}

MainChart.defaultProps = {}

export default MainChart
